import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { BREAKPOINT } from '@farewill/ui/tokens'
import PropTypes from 'prop-types'
import { debounce } from 'lodash-es'

import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api'
import { P } from '@farewill/ui'
import { GATSBY_GOOGLE_MAPS_API_KEY } from 'config'

import getMarkerIcon from 'lib/maps/getMarkerIcon'
import LoaderSpinner from 'components/LoaderSpinner'

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  div {
    &:focus {
      outline: none;
    }
  }
`

const containerHeightMobile = 352

const center = { lat: 51.509865, lng: -0.118092 }

const formatCoordinate = (coordinate) => {
  if (typeof coordinate !== 'string') return coordinate
  const decimalPlaces = coordinate.split('.')[1].length || 0
  return parseFloat(coordinate).toFixed(decimalPlaces)
}

const Map = ({ crematoriumsList, showMapMarker }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GATSBY_GOOGLE_MAPS_API_KEY,
  })

  const [bounds, setBounds] = useState()
  const [hoveredVenue, setHoveredVenue] = useState(null)
  const [selectedVenue, setSelectedVenue] = useState(null)
  const [windowWidth, setWindowWidth] = useState(null)
  const [containerHeight, setContainerHeight] = useState(containerHeightMobile)

  const containerStyle = {
    width: '100%',
    height: containerHeight,
  }

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      const windowInnerWidth =
        typeof window === 'undefined' ? null : window.innerWidth
      setWindowWidth(windowInnerWidth)
    }, 250)

    // Set initial windowWidth so that correct path etc is also set.
    setWindowWidth(typeof window === 'undefined' ? null : window.innerWidth)

    // Set listener for windowWidth
    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [])

  useEffect(() => {
    if (windowWidth && windowWidth >= BREAKPOINT.M) {
      setContainerHeight('520px')
    } else if (windowWidth && windowWidth < BREAKPOINT.M) {
      setContainerHeight('352px')
    }
  }, [windowWidth])

  const handleMarkerUpdate = (event, venue) => {
    setSelectedVenue(venue)
    event.domEvent.target.parentNode.focus()
  }

  useEffect(() => {
    if (isLoaded) {
      /* eslint-disable-next-line no-undef  */
      const initBounds = new google.maps.LatLngBounds()

      crematoriumsList.map((venue) =>
        initBounds.extend(
          /* eslint-disable-next-line no-undef  */
          new google.maps.LatLng(
            formatCoordinate(venue.latitude),
            formatCoordinate(venue.longitude)
          )
        )
      )
      setBounds(initBounds)
    }
  }, [isLoaded])

  const onMapLoad = (map) => {
    if (!bounds) return
    map.fitBounds(bounds)
    if (crematoriumsList.length <= 1) {
      /* eslint-disable-next-line no-undef  */
      google.maps.event.addListenerOnce(map, 'idle', () => {
        map.setZoom(10)
      })
    }
  }

  if (loadError) return 'error loading maps'
  if (!isLoaded)
    return (
      <div>
        <LoaderSpinner centered height="400px" />
      </div>
    )

  return (
    <StyledWrapper>
      <GoogleMap
        onLoad={onMapLoad}
        center={center}
        mapContainerStyle={containerStyle}
        zoom={8}
      >
        {showMapMarker &&
          crematoriumsList.map((venue) => (
            <Marker
              onClick={(event) => handleMarkerUpdate(event, venue)}
              key={venue.id}
              position={{
                lat: formatCoordinate(venue.latitude),
                lng: formatCoordinate(venue.longitude),
              }}
              onMouseOver={() => setHoveredVenue(venue)}
              onMouseOut={() => setHoveredVenue()}
              icon={getMarkerIcon(venue.id, selectedVenue, hoveredVenue, venue)}
            >
              {selectedVenue !== null && (
                <>
                  <InfoWindow
                    onCloseClick={() => {
                      setSelectedVenue(null)
                    }}
                    position={{
                      lat: formatCoordinate(selectedVenue?.latitude),
                      lng: formatCoordinate(selectedVenue?.longitude),
                    }}
                  >
                    <P size="S">{selectedVenue.name}</P>
                  </InfoWindow>
                </>
              )}
            </Marker>
          ))}
      </GoogleMap>
    </StyledWrapper>
  )
}

Map.propTypes = {
  crematoriumsList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.oneOf([
        'Crematorium',
        'Reception',
        'Dignity Crematorium',
      ]),
      image: PropTypes.string,
      title: PropTypes.string,
      address: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      backstageID: PropTypes.string,
      description: PropTypes.shape({
        description: PropTypes.string,
      }),
    })
  ).isRequired,
  selectedVenue: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    longitude: PropTypes.number.isRequired,
    latitude: PropTypes.number.isRequired,
  }),
  showMapMarker: PropTypes.bool,
}
Map.defaultProps = {
  selectedVenue: null,
  showMapMarker: false,
}

export default React.memo(Map)
